import React, { CSSProperties } from "react";
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { successGreen, errorRed, warningYellow } from "../theme";

export type Rating = "positive" | "negative" | "neutral";

// Single source of truth for "what does this rating look like" — an icon
// choice, a color, and (for neutral) a rotation — shared by the interactive
// picker (Footer.tsx) and the read-only summary display (interview/Modals.tsx).
// Before this, the two had quietly drifted: different color values for the
// same ratings, and two different icons standing in for "neutral" (thumbs-up
// rotated vs. thumbs-down rotated).
const RATING_ICONS: Record<Rating, typeof FaThumbsUp> = {
  positive: FaThumbsUp,
  negative: FaThumbsDown,
  neutral: FaThumbsUp,
};

export const RATING_COLORS: Record<Rating, string> = {
  positive: successGreen,
  negative: errorRed,
  neutral: warningYellow,
};

const RATING_HOVER_CLASS: Record<Rating, string> = {
  positive: "hover-green",
  negative: "hover-red",
  neutral: "hover-yellow",
};

type RatingIconProps = {
  rating: Rating;
  color?: string;
  style?: CSSProperties;
};

/** Icon-only, for read-only display (e.g. the admin review summary). */
export function RatingIcon({ rating, color, style }: RatingIconProps) {
  const Icon = RATING_ICONS[rating];
  const rotation: CSSProperties =
    rating === "neutral" ? { transform: "rotate(-90deg)" } : {};
  return <Icon color={color ?? RATING_COLORS[rating]} style={{ ...rotation, ...style }} />;
}

type RatingThumbButtonProps = {
  rating: Rating;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
};

const wrapperStyle: CSSProperties = {
  borderRadius: 50,
  height: 40,
  minHeight: 40,
  width: 40,
  minWidth: 40,
  marginLeft: 4,
  marginRight: 4,
};

/** Clickable, for the interactive rating picker (Footer.tsx). */
export function RatingThumbButton({ rating, selected, disabled, onClick }: RatingThumbButtonProps) {
  const color = RATING_COLORS[rating];
  return (
    <div
      className={`hor-center ${RATING_HOVER_CLASS[rating]}`}
      style={{
        ...wrapperStyle,
        border: `2px solid ${color}`,
        background: selected ? color : "none",
        cursor: disabled ? "not-allowed" : selected ? "default" : "pointer",
      }}
      onClick={onClick}
    >
      <RatingIcon rating={rating} color="white" style={{ fontSize: 18 }} />
    </div>
  );
}
